import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Lightweight, GPU-friendly ambient background: blurred tricolor glow blobs,
// an animated light-wave SVG and a sparse floating particle field.
export const Background = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll();
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 120]);

  // sparse particles rendered once
  const particles = useRef(
    Array.from({ length: 26 }).map(() => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2.5 + 1,
      delay: Math.random() * 6,
      dur: Math.random() * 6 + 6,
    }))
  );

  useEffect(() => {}, []);

  return (
    <div ref={ref} className="fixed inset-0 -z-10 overflow-hidden bg-zinc-950">
      {/* base radial vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(255,122,0,0.10),transparent_55%)]" />

      {/* tricolor glow blobs */}
      <motion.div
        style={{ y: y1 }}
        className="absolute -top-40 left-1/2 h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-indril-saffron/25 blur-[140px] animate-aurora"
      />
      <motion.div
        style={{ y: y2 }}
        className="absolute top-1/3 -left-40 h-[34rem] w-[34rem] rounded-full bg-indril-pink/15 blur-[150px] animate-aurora"
      />
      <motion.div
        style={{ y: y1 }}
        className="absolute bottom-0 -right-40 h-[36rem] w-[36rem] rounded-full bg-indril-green/12 blur-[150px] animate-aurora"
      />

      {/* light waves */}
      <svg
        className="absolute bottom-0 left-0 w-full opacity-40"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="wave" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF7A00" stopOpacity="0.0" />
            <stop offset="50%" stopColor="#FF7A00" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FF7A00" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        <motion.path
          d="M0,192 C320,120 520,260 720,200 C920,140 1120,60 1440,160"
          fill="none"
          stroke="url(#wave)"
          strokeWidth="1.5"
          animate={{ opacity: [0.3, 0.75, 0.3], y: [0, -16, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      </svg>

      {/* floating particles */}
      {particles.current.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-white/40"
          style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size }}
          animate={{ opacity: [0.1, 0.6, 0.1], y: [0, -24, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {/* grid lines */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(80%_60%_at_50%_30%,black,transparent)]" />
    </div>
  );
};
